import { trpc } from "@/helpers/trpc";

/**
 * Hook to manage the state of a donors list block
 * @param {Object} params - The parameters object
 * @param {string} params.donationFormId - The ID of the donation form
 * @param {boolean} params.enabled - Whether the donors list block is enabled (e.g. if the form has a target)
 * @returns {Object} An object containing:
 * - isLoading: Boolean indicating if the donations are currently being fetched
 * - hasDonations: True if there are donations to display
 * - latestDonations: Array of formatted donation objects with dates and amounts
 */
export const useThemedDonorsListBlockState = ({
  donationFormId,
  enabled = true,
}: {
  donationFormId: string;
  enabled?: boolean;
}) => {
  const { data: getLatestDonationsResponse, isFetching } = trpc.form_getLatestDonations.useQuery(
    {
      formId: donationFormId,
      limit: 4,
      cursor: 0,
    },
    {
      enabled,
      initialData: { items: [] },
    }
  );

  const latestDonations =
    getLatestDonationsResponse?.items?.map((donation) => ({
      ...donation,
      donatedOn: new Date(donation.donatedOn),
      donationAmount: donation.amount,
      donationCurrency: donation.currency,
    })) ?? [];

  const hasDonations = enabled && latestDonations.length > 0;

  return {
    isLoading: isFetching,
    latestDonations,
    hasDonations,
    hasMoreDonations: !!getLatestDonationsResponse?.nextCursor,
  };
};
